import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import IntegrationsTab from './IntegrationsTab';
import {
  ArrowLeft,
  Save,
  Plus,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  Shield,
  Settings,
  Database,
  Key,
  HardDrive,
  Mail,
  Link,
  ToggleRight,
  Crown,
  User,
  ChevronDown,
  LogOut,
  History,
  AlertCircle,
  CheckCircle,
  Server,
  RefreshCw,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';

// Using relative URLs
const API = '/api';

// Icon mapping for categories
const categoryIcons = {
  database: Database,
  api_keys: Key,
  storage: HardDrive,
  email: Mail,
  urls: Link,
  feature_flags: ToggleRight,
  other: Settings
};

// AWS Regions list
const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'af-south-1', label: 'Africa (Cape Town)' },
  { value: 'ap-east-1', label: 'Asia Pacific (Hong Kong)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-south-2', label: 'Asia Pacific (Hyderabad)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ap-southeast-3', label: 'Asia Pacific (Jakarta)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'ap-northeast-3', label: 'Asia Pacific (Osaka)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'eu-central-2', label: 'Europe (Zurich)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-west-2', label: 'Europe (London)' },
  { value: 'eu-west-3', label: 'Europe (Paris)' },
  { value: 'eu-south-1', label: 'Europe (Milan)' },
  { value: 'eu-south-2', label: 'Europe (Spain)' },
  { value: 'eu-north-1', label: 'Europe (Stockholm)' },
  { value: 'me-south-1', label: 'Middle East (Bahrain)' },
  { value: 'me-central-1', label: 'Middle East (UAE)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
];

// Variables that should use dropdown selectors
const DROPDOWN_VARIABLES = {
  'AWS_S3_REGION': AWS_REGIONS
};

const EnvironmentSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [variables, setVariables] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [envInfo, setEnvInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('variables');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [revealedValues, setRevealedValues] = useState({});
  
  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    category: 'other',
    description: '',
    is_sensitive: true,
    is_required: false,
    dev_value: '',
    prod_value: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [varsResponse, envResponse, logsResponse] = await Promise.all([
        axios.get(`${API}/admin/env-variables`, { headers }),
        axios.get(`${API}/admin/env-info`, { headers }),
        axios.get(`${API}/admin/env-audit-logs`, { headers })
      ]);
      
      setVariables(varsResponse.data.variables || []);
      setEnvInfo(envResponse.data);
      setAuditLogs(logsResponse.data.logs || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load environment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVariable = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/admin/env-variables`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Variable created successfully');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create variable';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateVariable = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const updateData = {
        category: formData.category,
        description: formData.description,
        is_required: formData.is_required
      };
      
      // Only include values if they were changed
      if (formData.dev_value && formData.dev_value !== '••••••••') {
        updateData.dev_value = formData.dev_value;
      }
      if (formData.prod_value && formData.prod_value !== '••••••••') {
        updateData.prod_value = formData.prod_value;
      }
      
      await axios.put(`${API}/admin/env-variables/${selectedVariable.name}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Variable updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update variable';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVariable = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API}/admin/env-variables/${selectedVariable.name}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Variable deleted successfully');
      setShowDeleteDialog(false);
      setSelectedVariable(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete variable';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRevealValue = async (varName, environment) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/env-variables/${varName}/reveal`,
        { environment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const key = `${varName}_${environment}`;
      setRevealedValues(prev => ({
        ...prev,
        [key]: response.data.value
      }));
      
      // Auto-hide after 30 seconds
      setTimeout(() => {
        setRevealedValues(prev => {
          const updated = { ...prev };
          delete updated[key];
          return updated;
        });
      }, 30000);
    } catch (error) {
      toast.error('Failed to reveal value');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'other',
      description: '',
      is_sensitive: true,
      is_required: false,
      dev_value: '',
      prod_value: ''
    });
    setSelectedVariable(null);
  };

  const openEditDialog = (variable) => {
    setSelectedVariable(variable);
    setFormData({
      name: variable.name,
      category: variable.category,
      description: variable.description || '',
      is_sensitive: variable.is_sensitive,
      is_required: variable.is_required,
      dev_value: variable.dev_value || '',
      prod_value: variable.prod_value || ''
    });
    setShowEditDialog(true);
  };

  const filteredVariables = selectedCategory === 'all'
    ? variables
    : variables.filter(v => v.category === selectedCategory);

  const getCategoryIcon = (category) => {
    const IconComponent = categoryIcons[category] || Settings;
    return <IconComponent className="w-4 h-4" />;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading environment settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-6">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Environment Settings</h1>
                  <p className="text-sm text-slate-600">Manage application configuration variables</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Environment Badge */}
              {envInfo && (
                <Badge 
                  className={`${
                    envInfo.is_production 
                      ? 'bg-red-100 text-red-700 border-red-200' 
                      : 'bg-blue-100 text-blue-700 border-blue-200'
                  }`}
                >
                  <Server className="w-3 h-3 mr-1" />
                  {envInfo.is_production ? 'PRODUCTION' : 'DEVELOPMENT'}
                </Badge>
              )}
              
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = '/login';
                    }}
                    className="text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Environment Info Banner */}
        {envInfo && (
          <Card className="mb-6 border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${
                    envInfo.is_production ? 'bg-red-500' : 'bg-green-500'
                  } animate-pulse`}></div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Currently running in <span className="font-bold">
                        {envInfo.is_production ? 'Production' : 'Development'}
                      </span> mode
                    </p>
                    <p className="text-xs text-slate-500">
                      Detection: {envInfo.detection_method === 'domain' ? 'Auto (domain-based)' : 'Explicit (ENVIRONMENT variable)'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="variables" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              Variables
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2" data-testid="env-tab-integrations">
              <Globe className="w-4 h-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="variables">
            {/* Category Filter & Add Button */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">Filter by category:</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                    <SelectItem value="api_keys">API Keys</SelectItem>
                    <SelectItem value="storage">Storage</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="urls">URLs</SelectItem>
                    <SelectItem value="feature_flags">Feature Flags</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Variable
              </Button>
            </div>

            {/* Variables Table */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="border-b bg-slate-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    Environment Variables
                  </CardTitle>
                  <Badge variant="outline">
                    {filteredVariables.length} variables
                  </Badge>
                </div>
                <CardDescription>
                  Configure different values for Development and Production environments
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Variable Name</TableHead>
                      <TableHead className="w-[120px]">Category</TableHead>
                      <TableHead>DEV Value</TableHead>
                      <TableHead>PROD Value</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredVariables.map((variable) => (
                      <TableRow key={variable.name}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
                              {variable.name}
                            </code>
                            {variable.is_required && (
                              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                Required
                              </Badge>
                            )}
                            {variable.is_bootstrap && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                Bootstrap
                              </Badge>
                            )}
                          </div>
                          {variable.description && (
                            <p className="text-xs text-slate-500 mt-1">{variable.description}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            {getCategoryIcon(variable.category)}
                            <span className="capitalize">{variable.category?.replace('_', ' ')}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ValueCell
                            variable={variable}
                            environment="dev"
                            revealedValues={revealedValues}
                            onReveal={handleRevealValue}
                          />
                        </TableCell>
                        <TableCell>
                          <ValueCell
                            variable={variable}
                            environment="prod"
                            revealedValues={revealedValues}
                            onReveal={handleRevealValue}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(variable)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {!variable.is_bootstrap && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedVariable(variable);
                                  setShowDeleteDialog(true);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredVariables.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No variables found. Click "Add Variable" to create one.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations">
            <IntegrationsTab />
          </TabsContent>

          <TabsContent value="audit">
            <Card className="border-0 shadow-lg">
              <CardHeader className="border-b bg-slate-50">
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-emerald-600" />
                  Audit Log
                </CardTitle>
                <CardDescription>
                  Track all changes to environment variables
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Variable</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log, index) => (
                      <TableRow key={index}>
                        <TableCell className="text-sm">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`
                            ${log.action === 'create' ? 'bg-green-100 text-green-700' : ''}
                            ${log.action === 'update' ? 'bg-blue-100 text-blue-700' : ''}
                            ${log.action === 'delete' ? 'bg-red-100 text-red-700' : ''}
                            ${log.action === 'reveal' ? 'bg-amber-100 text-amber-700' : ''}
                          `}>
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
                            {log.variable_name}
                          </code>
                        </TableCell>
                        <TableCell className="text-sm">{log.user_email}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {log.details?.changes?.join(', ') || log.details?.environment || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {auditLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No audit logs yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Variable Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Environment Variable</DialogTitle>
            <DialogDescription>
              Create a new configuration variable with separate values for Development and Production.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Variable Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                placeholder="e.g., STRIPE_API_KEY"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-slate-500 mt-1">Use UPPER_SNAKE_CASE format</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="api_keys">API Keys</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                    <SelectItem value="storage">Storage</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="urls">URLs</SelectItem>
                    <SelectItem value="feature_flags">Feature Flags</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="sensitive">Sensitive</Label>
                  <Switch
                    id="sensitive"
                    checked={formData.is_sensitive}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_sensitive: v }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="required">Required</Label>
                  <Switch
                    id="required"
                    checked={formData.is_required}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_required: v }))}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What is this variable used for?"
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">Values</h4>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="dev_value" className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700">DEV</Badge>
                    Development Value
                  </Label>
                  <Input
                    id="dev_value"
                    type={formData.is_sensitive ? 'password' : 'text'}
                    value={formData.dev_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, dev_value: e.target.value }))}
                    placeholder="Development environment value"
                    className="mt-1 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="prod_value" className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-700">PROD</Badge>
                    Production Value
                  </Label>
                  <Input
                    id="prod_value"
                    type={formData.is_sensitive ? 'password' : 'text'}
                    value={formData.prod_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, prod_value: e.target.value }))}
                    placeholder="Production environment value"
                    className="mt-1 font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddVariable} disabled={saving || !formData.name}>
              {saving ? 'Creating...' : 'Create Variable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Variable Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Variable: {selectedVariable?.name}</DialogTitle>
            <DialogDescription>
              Update the configuration for this variable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_category">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="api_keys">API Keys</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                    <SelectItem value="storage">Storage</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="urls">URLs</SelectItem>
                    <SelectItem value="feature_flags">Feature Flags</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between pt-6">
                <Label htmlFor="edit_required">Required</Label>
                <Switch
                  id="edit_required"
                  checked={formData.is_required}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_required: v }))}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">Update Values</h4>
              <p className="text-xs text-slate-500 mb-3">Leave blank to keep existing value</p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit_dev_value" className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700">DEV</Badge>
                    Development Value
                    {selectedVariable?.dev_has_value && (
                      <CheckCircle className="w-3 h-3 text-green-500" />
                    )}
                  </Label>
                  {DROPDOWN_VARIABLES[selectedVariable?.name] ? (
                    <Select 
                      value={formData.dev_value} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, dev_value: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a region..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {DROPDOWN_VARIABLES[selectedVariable?.name].map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label} ({option.value})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="edit_dev_value"
                      type={selectedVariable?.is_sensitive ? 'password' : 'text'}
                      value={formData.dev_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, dev_value: e.target.value }))}
                      placeholder={selectedVariable?.dev_has_value ? 'Leave blank to keep current' : 'Enter value'}
                      className="mt-1 font-mono text-sm"
                    />
                  )}
                </div>
                <div>
                  <Label htmlFor="edit_prod_value" className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-700">PROD</Badge>
                    Production Value
                    {selectedVariable?.prod_has_value && (
                      <CheckCircle className="w-3 h-3 text-green-500" />
                    )}
                  </Label>
                  {DROPDOWN_VARIABLES[selectedVariable?.name] ? (
                    <Select 
                      value={formData.prod_value} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, prod_value: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a region..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {DROPDOWN_VARIABLES[selectedVariable?.name].map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label} ({option.value})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="edit_prod_value"
                      type={selectedVariable?.is_sensitive ? 'password' : 'text'}
                      value={formData.prod_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, prod_value: e.target.value }))}
                      placeholder={selectedVariable?.prod_has_value ? 'Leave blank to keep current' : 'Enter value'}
                      className="mt-1 font-mono text-sm"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateVariable} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Variable
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <code className="font-mono bg-slate-100 px-2 py-1 rounded">{selectedVariable?.name}</code>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteVariable} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete Variable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Value Cell Component
const ValueCell = ({ variable, environment, revealedValues, onReveal }) => {
  const key = `${variable.name}_${environment}`;
  const hasValue = environment === 'dev' ? variable.dev_has_value : variable.prod_has_value;
  const displayValue = environment === 'dev' ? variable.dev_value : variable.prod_value;
  const revealedValue = revealedValues[key];
  
  if (!hasValue) {
    return <span className="text-slate-400 text-sm italic">Not set</span>;
  }
  
  if (variable.is_sensitive) {
    if (revealedValue) {
      return (
        <div className="flex items-center gap-2">
          <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded max-w-[200px] truncate">
            {revealedValue}
          </code>
          <Badge className="bg-amber-100 text-amber-700 text-xs">Visible</Badge>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm">••••••••</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onReveal(variable.name, environment)}
          className="h-6 px-2"
        >
          <Eye className="w-3 h-3" />
        </Button>
      </div>
    );
  }
  
  return (
    <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded max-w-[200px] truncate block">
      {displayValue}
    </code>
  );
};

export default EnvironmentSettings;
